import * as React from "react";
import { useState, useEffect } from "react";
import { HelpCircle, X, Clock, Search, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Loader2, RefreshCw, Database, MessageCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const WorkloadHelpButton = ({ lastClickupSync }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [clickupConnector, setClickupConnector] = useState(null);
  const [taskId, setTaskId] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [openFaqs, setOpenFaqs] = useState({});

  // Fetch ClickUp connector status from Fivetran metadata
  useEffect(() => {
    const fetchClickupConnector = async () => {
      try {
        const response = await axios.get(`${API}/fivetran/connector/clickup`);
        if (response.data.found) {
          setClickupConnector(response.data);
        }
      } catch (error) {
        console.error("Error fetching ClickUp connector:", error);
      }
    };

    if (isOpen) {
      fetchClickupConnector();
    }
  }, [isOpen]);

  // Calculate countdown to next sync using real Fivetran data
  useEffect(() => {
    const calculateCountdown = () => {
      // Use Fivetran data if available, otherwise fallback to lastClickupSync
      const lastSync = clickupConnector?.last_sync || lastClickupSync;
      const syncFreqMinutes = clickupConnector?.sync_frequency_minutes || 120; // Default 2h
      
      if (!lastSync) {
        setCountdown(null);
        return;
      }

      const lastSyncDate = new Date(lastSync);
      const nextSync = new Date(lastSyncDate.getTime() + syncFreqMinutes * 60 * 1000);
      const now = new Date();
      const diff = nextSync - now;

      if (diff <= 0) {
        // Next sync should have happened, estimate next window
        const minutesSinceSync = (now - lastSyncDate) / (1000 * 60);
        const nextWindow = Math.ceil(minutesSinceSync / syncFreqMinutes) * syncFreqMinutes;
        const estimatedNext = new Date(lastSyncDate.getTime() + nextWindow * 60 * 1000);
        const newDiff = estimatedNext - now;
        
        if (newDiff > 0) {
          const hours = Math.floor(newDiff / (1000 * 60 * 60));
          const minutes = Math.floor((newDiff % (1000 * 60 * 60)) / (1000 * 60));
          setCountdown({ hours, minutes, isOverdue: false, syncFreqMinutes });
        } else {
          setCountdown({ hours: 0, minutes: 0, isOverdue: true, syncFreqMinutes });
        }
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown({ hours, minutes, isOverdue: false, syncFreqMinutes });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastClickupSync, clickupConnector]);

  const toggleFaq = (id) => {
    setOpenFaqs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleVerifyTask = async () => {
    if (!taskId.trim()) {
      toast.error("Ingresa un ID de tarea");
      return;
    }

    setIsVerifying(true);
    setVerifyResult(null);

    try {
      const response = await axios.get(`${API}/workload/verify-task/${taskId.trim()}`);
      setVerifyResult(response.data);
    } catch (error) {
      console.error("Error verifying task:", error);
      setVerifyResult({
        exists: false,
        message: "Error al consultar Databricks. Intenta de nuevo más tarde."
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const formatSyncDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const faqs = [
    {
      id: "no-task",
      question: "¿Por qué no veo mi tarea en el sistema?",
      answer: (
        <div className="space-y-3 text-sm text-slate-600">
          <p>Hay varias razones por las que tu tarea podría no aparecer aún. No te preocupes, aquí te explicamos las más comunes:</p>
          
          <div className="space-y-2">
            <div className="flex gap-2 p-2 bg-amber-50 rounded-lg">
              <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">¿La creaste hace menos de 2 horas?</p>
                <p className="text-amber-700 text-xs">Nuestro sistema no trabaja en tiempo real. Los datos de ClickUp se sincronizan cada 2 horas aproximadamente. Si acabas de crear la tarea, deberás esperar a la próxima ventana de actualización.</p>
              </div>
            </div>
            
            <div className="flex gap-2 p-2 bg-blue-50 rounded-lg">
              <RefreshCw className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-800">¿La modificaste recientemente?</p>
                <p className="text-blue-700 text-xs">Similar al caso anterior, si cambiaste el estado, asignación o cualquier campo, los cambios se reflejarán en la próxima sincronización.</p>
              </div>
            </div>
            
            <div className="flex gap-2 p-2 bg-purple-50 rounded-lg">
              <AlertCircle className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-purple-800">¿Está en un espacio privado?</p>
                <p className="text-purple-700 text-xs">Si la tarea está en un espacio de ClickUp que es privado, es posible que el sistema no tenga acceso. Contacta al administrador para que verifique los permisos a nivel de sistema.</p>
              </div>
            </div>
            
            <div className="flex gap-2 p-2 bg-emerald-50 rounded-lg">
              <Database className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">¿Ya intentaste forzar el caché?</p>
                <p className="text-emerald-700 text-xs">El caché interno de Datahub se refresca cada hora para no saturar el sistema. Prueba usar el botón "Forzar Actualización" en la parte superior de la pantalla.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "verify",
      question: "¿Cómo puedo verificar si mi tarea existe en el sistema?",
      answer: (
        <div className="space-y-3 text-sm text-slate-600">
          <p>Puedes usar el verificador de tareas que está más abajo en esta ventana. Solo necesitas:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Copiar el ID de tu tarea desde ClickUp (lo encuentras en la URL de la tarea)</li>
            <li>Pegarlo en el campo de búsqueda</li>
            <li>Hacer clic en "Verificar"</li>
          </ol>
          <p className="text-xs text-slate-500 mt-2">
            💡 <strong>Tip:</strong> El ID de la tarea es el código alfanumérico que aparece en la URL, por ejemplo: <code className="bg-slate-100 px-1 rounded">abc123xyz</code>
          </p>
        </div>
      )
    },
    {
      id: "sync-times",
      question: "¿Cada cuánto se actualizan los datos?",
      answer: (
        <div className="space-y-3 text-sm text-slate-600">
          <p>Los datos pasan por dos etapas de sincronización:</p>
          
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 font-bold text-xs">1</div>
              <div>
                <p className="font-medium">ClickUp → Databricks</p>
                <p className="text-xs text-slate-500">Cada ~2 horas (sincronización automática via Fivetran)</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center text-slate-400">
              <ChevronDown className="h-4 w-4" />
            </div>
            
            <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-xs">2</div>
              <div>
                <p className="font-medium">Databricks → Datahub</p>
                <p className="text-xs text-slate-500">Caché de 1 hora (puedes forzar actualización manual)</p>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-2">
            Esto significa que en el peor caso, un cambio en ClickUp podría tardar hasta ~3 horas en reflejarse aquí.
          </p>
        </div>
      )
    }
  ];

  return (
    <>
      {/* Floating Help Button - positioned above footer */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group"
      >
        <HelpCircle className="h-5 w-5" />
        <span className="font-medium">¿Algo no cuadra?</span>
      </button>

      {/* Help Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <MessageCircle className="h-5 w-5 text-orange-500" />
              Centro de Ayuda
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-5">
            {/* Countdown Card with Fivetran Status */}
            <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-100">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Próxima sincronización con ClickUp</p>
                    {countdown ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-bold text-blue-700">
                          {countdown.isOverdue ? (
                            "En cualquier momento..."
                          ) : (
                            `${countdown.hours}h ${countdown.minutes}min`
                          )}
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-500 text-sm">Calculando...</span>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span>Última sync: {formatSyncDate(clickupConnector?.last_sync || lastClickupSync)}</span>
                      {countdown?.syncFreqMinutes && (
                        <>
                          <span>•</span>
                          <span>Frecuencia: cada {Math.round(countdown.syncFreqMinutes / 60)}h</span>
                        </>
                      )}
                    </div>
                    {/* Connector Status */}
                    {clickupConnector && (
                      <div className="mt-2 flex items-center gap-2">
                        {clickupConnector.status === "HEALTHY" ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Conector Saludable
                          </Badge>
                        ) : clickupConnector.status === "WARNINGS" ? (
                          <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {clickupConnector.warnings_24h} advertencias (24h)
                          </Badge>
                        ) : clickupConnector.status === "PAUSED" ? (
                          <Badge className="bg-slate-100 text-slate-600 border border-slate-300 text-xs">
                            Conector Pausado
                          </Badge>
                        ) : clickupConnector.status === "DELAYED" ? (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Sync Retrasada
                          </Badge>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQs Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Preguntas Frecuentes
              </h3>
              
              <div className="space-y-2">
                {faqs.map(faq => (
                  <Collapsible
                    key={faq.id}
                    open={openFaqs[faq.id]}
                    onOpenChange={() => toggleFaq(faq.id)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
                        <span className="text-sm font-medium text-slate-700 text-left">
                          {faq.question}
                        </span>
                        {openFaqs[faq.id] ? (
                          <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-3 py-3 bg-white border border-t-0 border-slate-100 rounded-b-lg">
                        {faq.answer}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>

            {/* Task Verifier Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Verificador de Tareas
              </h3>
              
              <Card>
                <CardContent className="py-4">
                  <p className="text-xs text-slate-500 mb-3">
                    Ingresa el ID de tu tarea para verificar si existe en Databricks. 
                    Puedes encontrar el ID en la URL de la tarea en ClickUp.
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ej: abc123xyz o #abc123xyz"
                      value={taskId}
                      onChange={(e) => setTaskId(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleVerifyTask()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleVerifyTask}
                      disabled={isVerifying || !taskId.trim()}
                      className="gap-2"
                    >
                      {isVerifying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                      Verificar
                    </Button>
                  </div>

                  {/* Verification Result */}
                  {verifyResult && (
                    <div className={`mt-3 p-3 rounded-lg ${verifyResult.exists ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-start gap-2">
                        {verifyResult.exists ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className={`font-medium ${verifyResult.exists ? 'text-emerald-800' : 'text-red-800'}`}>
                            {verifyResult.message}
                          </p>
                          {verifyResult.exists && (
                            <div className="mt-2 space-y-1 text-sm">
                              <p className="text-emerald-700">
                                <strong>Tarea:</strong> {verifyResult.task_name}
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                  {verifyResult.item_type}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {verifyResult.status}
                                </Badge>
                                {verifyResult.space_name && (
                                  <Badge variant="outline" className="text-xs">
                                    📁 {verifyResult.space_name}
                                  </Badge>
                                )}
                              </div>
                              {verifyResult.status_type === 'closed' && (
                                <p className="text-xs text-amber-600 mt-1">
                                  ⚠️ Esta tarea está cerrada, por eso no aparece en el análisis de carga activa.
                                </p>
                              )}
                            </div>
                          )}
                          {!verifyResult.exists && (
                            <p className="text-xs text-red-600 mt-1">
                              Verifica que el ID sea correcto o espera a la próxima sincronización.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
